<template>
  <div>
    <div class="flex flex-wrap gap-3 mb-6 justify-end items-center">
      <button @click="createModal"
        class="bg-primary-button hover:bg-primary-button-hover text-white px-4 py-2 rounded transition-all duration-200 text-sm">
        + Stok Ekle
      </button>

      <input type="text" placeholder="Ara..." v-model="searchQuery"
        class="h-10 w-[300px] rounded-lg border border-light-border dark:border-gray-600 bg-primary-bg dark:bg-gray-800 px-4 text-sm text-primary-text dark:text-gray-100  focus:border-indigo-500 focus:ring focus:ring-blue-100/50" />
    </div>

    <div
      class="overflow-hidden rounded-md border border-light-border dark:border-dark-border bg-primary-bg dark:bg-primary-dark-bg pb-3 shadow-md">

      <div v-if="isLoadingStocks" class="flex flex-col justify-center items-center py-10">
        <svg class="animate-spin h-5 w-5 text-gray-400 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
        </svg>
        <span class="text-gray-400 text-sm">Stoklar Yükleniyor...</span>
      </div>

      <div v-else class="w-full overflow-x-auto">
        <table class="min-w-full text-left text-sm text-secondary-text dark:text-gray-300">
          <thead class="bg-gray-100 dark:bg-gray-800 font-medium">
            <tr>
              <th class="px-3 md:px-6 py-2 md:py-4">Stok Adı</th>
              <th class="px-3 md:px-6 py-2 md:py-4">Stok Miktarı</th>
              <th class="px-3 md:px-6 py-2 md:py-4">Durum</th>
              <th class="px-3 md:px-6 py-2 md:py-4">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="stock in paginatedStocks" :key="stock.id"
              class="border-b transition hover:light-hover dark:hover:dark-hover">
              <td class="px-3 md:px-6 py-2 md:py-4 font-medium text-primary-text dark:text-gray-100">
                {{ stock.name }}
              </td>
              <td
                :class="stock.amount <= stock.alarm_amount ? 'text-red-400  font-medium' : 'text-primary-text dark:text-gray-100 font-medium'">
                {{ stock.amount }} {{ stock.unit?.name || '' }}
              </td>
              <td class="px-3 md:px-6 py-2 md:py-4">
                <span v-if="stock.should_alarm && stock.amount <= stock.alarm_amount"
                  class="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-200 dark:text-red-900">
                  Kritik Stok
                </span>
                <span v-else
                  class="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-900'">
                  Normal
                </span>
              </td>
              <td class="px-3 md:px-6 py-2 md:py-4 flex gap-2">
                <button @click="editModal(stock)" class="relative group">
                  <PencilSquareIcon
                    class="w-5 h-5 text-gray-500 hover:text-secondary-text dark:text-gray-300 dark:hover:text-white" />
                  <span
                    class="absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition bg-black text-white text-xs px-2 py-1 rounded">Düzenle</span>
                </button>
                <button @click="deleteModal(stock)" class="relative group">
                  <TrashIcon class="w-5 h-5 text-red-500 hover:text-red-600" />
                  <span
                    class="absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition bg-black text-white text-xs px-2 py-1 rounded">Sil</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <AppPagination :currentPage="currentPage" :totalPages="totalPages" @prev-page="currentPage--"
        @next-page="currentPage++" @go-to-page="page => currentPage = page" />
    </div>

    <div v-if="isModalOpen" @click.self="isModalOpen = false"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 sm:p-0">
      <div
        class="bg-primary-bg dark:bg-primary-dark-bg p-6 sm:p-10 rounded-lg border border-light-border dark:border-dark-border w-full max-w-md sm:max-w-2xl max-h-[90vh] overflow-y-auto custom-scroll relative">
        <button @click="isModalOpen = false"
          class="absolute top-2 right-2 p-1 text-gray-400 hover:text-secondary-text dark:text-gray-300 dark:hover:text-white">
          <XMarkIcon class="w-6 h-6" />
        </button>
        <h2
          class="flex items-center space-x-2 text-xl font-medium mb-4 border-b border-gray-300 text-primary-text dark:text-gray-100 pb-2">
          <span>{{ isEditing ? 'Stok Düzenle' : 'Yeni Stok Ekle' }}</span>
        </h2>
        <form @submit.prevent="addOrUpdateStock">
          <div class="my-4">
            <label class="block mb-2 text-sm font-medium text-primary-text dark:text-gray-100">Stok Adı</label>
            <input v-model="newStock.name" type="text"
              class="w-full p-2 border rounded border-light-border dark:border-dark-border bg-primary-bg dark:bg-gray-800 text-primary-text dark:text-gray-100" />
          </div>

          <div class="my-4">
            <label class="block mb-2 text-sm font-medium text-primary-text dark:text-gray-100">Stok Miktarı</label>
            <input v-model.number="newStock.amount" type="number"
              class="w-full p-2 border rounded border-light-border dark:border-dark-border bg-primary-bg dark:bg-gray-800 text-primary-text dark:text-gray-100" />
          </div>

          <div class="my-4">
            <label class="block mb-2 text-sm font-medium text-primary-text dark:text-gray-100">Birim</label>
            <div class="flex  items-center gap-3">
              <v-select v-model="newStock.unit_id" :options="units" label="name" :reduce="unit => unit.id"
                placeholder="Birim Seçiniz" class="bg-primary-bg dark:bg-gray-800 w-full" />

              <button @click="unitModal"
                class="bg-primary-button hover:bg-primary-button-hover text-white h-10 w-10 rounded text-xl transition"
                type="button">
                +
              </button>
            </div>
          </div>

          <div class="my-4 flex items-center gap-4">
            <label class="flex items-center text-sm font-medium text-primary-text dark:text-gray-100">
              <input type="checkbox" v-model="newStock.should_alarm"
                class="mr-2 rounded border-light-border dark:border-dark-border bg-primary-bg dark:bg-gray-800" />
              Kritik stok alarmı aktif olsun mu?
            </label>

            <select v-if="newStock.should_alarm" v-model="newStock.alarm_target"
              class="p-2 border rounded bg-primary-bg dark:bg-gray-800 text-sm border-light-border dark:border-dark-border">
              <option disabled value="">Lütfen seçiniz</option>
              <option value="minimum">Minimum</option>
              <option value="maximum">Maksimum</option>
            </select>
          </div>

          <div v-if="newStock.should_alarm" class="my-4">
            <label class="block mb-2 text-sm font-medium text-primary-text dark:text-gray-100">Kritik Stok Alarm
              Miktarı</label>
            <input v-model.number="newStock.alarm_amount" type="number" required
              class="w-full p-2 border rounded border-light-border dark:border-dark-border bg-primary-bg dark:bg-gray-800 text-primary-text dark:text-gray-100" />
          </div>

          <button type="submit"
            class="w-full bg-primary-button hover:bg-primary-button-hover text-white py-2 px-4 rounded transition">
            {{ isEditing ? 'Güncelle' : 'Kaydet' }}
          </button>
        </form>
      </div>
    </div>

    <div v-if="isUnitModalOpen" @click.self="isUnitModalOpen = false"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        class="bg-primary-bg dark:bg-primary-dark-bg  border border-light-border dark:border-dark-border p-6 rounded-lg w-full max-w-md relative m-5 sm:m-0">
        <button @click="isUnitModalOpen = false"
          class="absolute top-2 right-2 p-1 text-gray-400 hover:text-secondary-text dark:text-gray-300 dark:hover:text-white">
          <XMarkIcon class="w-6 h-6" />
        </button>
        <h2 class="text-xl font-medium mb-4 border-b border-gray-300 pb-2 text-primary-text dark:text-gray-100">
          Yeni Birim Ekle
        </h2>
        <input v-model="newUnitName" type="text"
          class="w-full p-2 border rounded border-light-border dark:border-dark-border bg-primary-bg dark:bg-gray-800 text-primary-text dark:text-gray-100 mb-4"
          placeholder="Örn: Ton, Adet" />

        <div class="flex justify-end gap-2">
          <button @click="addUnit"
            class="px-4 py-2 rounded bg-primary-button hover:bg-primary-button-hover text-white text-sm">
            Ekle
          </button>
        </div>
      </div>
    </div>

    <DeleteModal v-if="isDeleteModal" :item="selectedStock" message="Bu ürünü silmek istediğinizden emin misiniz?"
      @close="isDeleteModal = false" @confirm-delete="handleDelete" @click.self="isDeleteModal = false" />
  </div>
</template>

<script>
import { getApi, postApi, putApi, deleteApi } from "@/services/api";
import DeleteModal from '@/components/Modals/DeleteModal.vue';
import { PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import vSelect from 'vue3-select'
import 'vue3-select/dist/vue3-select.css'

export default {
  name: "StockPage",
  components: {
    DeleteModal,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon,
    vSelect,
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      searchQuery: "",
      stocks: [],
      units: [],
      isModalOpen: false,
      isEditing: false,
      editingStockId: null,
      newStock: {
        name: '',
        amount: null,
        unit_id: null,
        should_alarm: false,
        alarm_amount: null,
        alarm_target: "minimum",
      },
      selectedStock: null,
      isUnitModalOpen: false,
      newUnitName: '',
      isDeleteModal: false,
      isLoadingStocks: false,
      isLoadingUnits: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredStock.length / this.pageSize);
    },
    filteredStock() {
      const query = this.searchQuery.toLowerCase();
      return this.stocks.filter(p => {
        return (p.name || '').toLowerCase().includes(query);
      });
    },

    paginatedStocks() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredStock.slice(start, start + this.pageSize);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    }
  },
  async mounted() {
    await this.fetchUnits();
    await this.fetchStocks();
  },
  methods: {
    async fetchStocks() {
      this.isLoadingStocks = true;
      try {
        const response = await getApi("/stocks/");
        this.stocks = response || [];
      } catch (error) {
        console.error("Stocks çekilemedi:", error);
        this.stocks = [];
      } finally {
        this.isLoadingStocks = false;
      }
    },

    async fetchUnits() {
      this.isLoadingUnits = true;
      try {
        const response = await getApi("/units/");
        this.units = response || [];
      } catch (error) {
        console.error("Units çekilemedi:", error);
        this.units = [];
      } finally {
        this.isLoadingUnits = false;
      }
    },

    createModal() {
      this.isModalOpen = true;
      this.isEditing = false;
      this.newStock = {
        name: '',
        amount: 0,
        unit_id: null,
        should_alarm: false,
        alarm_amount: 0,
        alarm_target: '',
      };
    },

    editModal(stock) {
      this.isModalOpen = true;
      this.isEditing = true;
      this.editingStockId = stock.id;
      this.newStock = {
        name: stock.name,
        amount: stock.amount,
        unit_id: stock.unit?.id || null,
        should_alarm: stock.should_alarm,
        alarm_amount: stock.alarm_amount,
        alarm_target: stock.alarm_target ?? "",
      };
    },

    deleteModal(stock) {
      this.selectedStock = stock;
      this.isDeleteModal = true;
    },
    unitModal() {
      this.isUnitModalOpen = true;
      this.newUnitName = '';
    },
    closeModal() {
      this.isModalOpen = false;
      this.isEditing = false;
      this.newStock = {
        name: '',
        amount: null,
        unit_id: null,
        should_alarm: false,
        alarm_amount: null,
        alarm_target: "minimum",
      };
      this.editingStockId = null;
    },

    async addUnit() {
      if (this.newUnitName.trim() !== '') {
        try {
          await postApi("/units/", {
            name: this.newUnitName,
          });
          await this.fetchUnits();
          this.isUnitModalOpen = false;
        } catch (error) {
          console.error("Birim ekleme hatası:", error);
        }
      }
    },
    async addOrUpdateStock() {
      try {
        if (!this.newStock.name || this.newStock.amount === null || this.newStock.unit_id === null) {
          this.$toast.error("Lütfen tüm zorunlu alanları doldurun!");
          return;
        }

        if (this.newStock.amount < 0) {
          this.$toast.error("Miktar sıfırdan küçük olamaz!");
          return;
        }

        if (this.newStock.should_alarm) {
          if (this.newStock.alarm_amount === null || this.newStock.alarm_amount < 0) {
            this.$toast.error("Alarm miktarı sıfırdan küçük olamaz!");
            return;
          }
          if (!this.newStock.alarm_target) {
            this.$toast.error("Lütfen alarm tipi (minimum / maksimum) seçiniz!");
            return;
          }
        }

        const payload = {
          name: this.newStock.name,
          amount: this.newStock.amount,
          unit_id: this.newStock.unit_id,
          should_alarm: this.newStock.should_alarm,
          alarm_amount: this.newStock.should_alarm ? this.newStock.alarm_amount : null,
          alarm_target: this.newStock.should_alarm ? this.newStock.alarm_target : null,
        };

        if (this.isEditing) {
          await putApi(`/stocks/${this.editingStockId}`, payload);
          this.$toast.success("Stok başarıyla güncellendi!");
        } else {
          await postApi("/stocks/", payload);
          this.$toast.success("Yeni stok başarıyla eklendi!");
        }

        await this.fetchStocks();
        this.closeModal();

      } catch (error) {
        console.error("Stock ekleme/güncelleme hatası:", error);
        this.$toast.error("İşlem sırasında bir hata oluştu!");
      }
    },

    async handleDelete() {
      if (this.selectedStock) {
        try {
          await deleteApi(`/stocks/${this.selectedStock.id}`);
          this.$toast.success("Başarıyla silindi!");
          await this.fetchStocks();
          this.selectedStock = null;
          this.isDeleteModal = false;
        } catch (error) {
          console.error("Stock silme hatası:", error);
        }
      }
    }

  }
};
</script>
